import { ref, computed } from "vue";
import store from "@/store";
import ProfileService from "@/services/profile";
import EpigeneticsService from "@/services/epigenetics";
import { HEALTH } from "@/composables/useProfileDomain";

/**
 * Loads the account's switchable profiles and groups them the way the portal needs:
 *  - ancestry kits stay 1:1 with a barcode (one row each);
 *  - epigenetic kits are grouped by `rootProfileId` into ONE row per person, because the
 *    same person commonly has several epigenetic barcodes over time (trend tracking).
 *
 * This is the single implementation behind both the profile switcher (SwitchProfile.vue)
 * and the Ancestry/Health nav entry resolution, so the two never drift.
 *
 * Note (launch scope, decision "A"): there is no person-level link between a person's
 * ancestry and epigenetic profiles in php api — the only shared grouping is the account.
 * So `resolveDomainEntry` resolves at ACCOUNT level: a multi-person account with several
 * epigenetic roots yields `choose`, not an automatic swap.
 */
export function useAccountProfiles() {
  const loading = ref(false);
  const loaded = ref(false);
  const rows = ref([]); // merged switchable rows: ancestry kits + one row per epigenetic person

  async function load({ force = false } = {}) {
    if (loaded.value && !force) return rows.value;
    loading.value = true;
    try {
      // The kit list is re-read with the profiles rather than trusted from the persisted
      // store: a copy saved by an earlier session (possibly another account's, in the same
      // browser) would otherwise decide which profiles can be switched to, indefinitely.
      const [allProfiles, epiKits] = await Promise.all([
        ProfileService.getProfiles(),
        EpigeneticsService.getKits().catch(() => []),
        store.dispatch("FORCE_SET_ACCOUNT_TESTS"),
      ]);
      const accountTests = store.getters.accountTests ?? [];

      // barcode -> rootProfileId, from the epigenetics kits endpoint
      const epiRootMap = {};
      epiKits.forEach((k) => { epiRootMap[k.barcode] = k.rootProfileId; });

      const merged = accountTests.map((test) => ({
        ...allProfiles[test.barcode],
        ...test,
        rootProfileId: epiRootMap[test.barcode] ?? null,
      }));

      const nonEpi = merged.filter((p) => !p.barcode?.endsWith("E"));
      const epiItems = merged.filter((p) => p.barcode?.endsWith("E"));

      // Group epigenetic kits by rootProfileId (fall back to barcode) — one row per person.
      const epiGroups = new Map();
      epiItems.forEach((p) => {
        const key = p.rootProfileId ?? p.barcode;
        if (!epiGroups.has(key)) {
          epiGroups.set(key, { ...p, epiKits: [p], isEpigeneticsGroup: true });
        } else {
          epiGroups.get(key).epiKits.push(p);
        }
      });

      rows.value = [...nonEpi, ...epiGroups.values()];
      loaded.value = true;
      return rows.value;
    } finally {
      loading.value = false;
    }
  }

  const epigeneticGroups = computed(() => rows.value.filter((r) => r.isEpigeneticsGroup));
  const ancestryProfiles = computed(() => rows.value.filter((r) => !r.isEpigeneticsGroup));
  const hasEpigenetics = computed(() => epigeneticGroups.value.length > 0);
  const hasAncestry = computed(() => ancestryProfiles.value.length > 0);

  /**
   * Make `profile` the account's current profile (server-side default), and refresh the
   * profile + its tests. Routing/close is left to the caller so the same swap works from
   * the switcher modal and from a nav click. Mirrors SwitchProfile's original selectProfile.
   */
  async function switchToProfile(profile) {
    await Promise.all([
      ProfileService.setDefaultProfile(profile.permission.id),
      store.dispatch("SET_TEST_FROM_TESTS", profile.barcode),
      store.dispatch("SET_KIT_TESTS", profile.barcode),
    ]);
    await store.dispatch("fetchProfile");
  }

  /**
   * Resolve what should happen when the user asks to go to `targetDomain`:
   *  - `upsell` : the account owns nothing in that domain → offer to buy/add it;
   *  - `swap`   : exactly one target → switch straight to it (`target`);
   *  - `choose` : several targets (e.g. a family account) → let the user pick (`targets`).
   */
  async function resolveDomainEntry(targetDomain) {
    await load();
    const targets = targetDomain === HEALTH ? epigeneticGroups.value : ancestryProfiles.value;
    if (targets.length === 0) return { action: "upsell", domain: targetDomain };
    if (targets.length === 1) return { action: "swap", domain: targetDomain, target: targets[0] };
    return { action: "choose", domain: targetDomain, targets };
  }

  return {
    loading,
    loaded,
    rows,
    epigeneticGroups,
    ancestryProfiles,
    hasEpigenetics,
    hasAncestry,
    load,
    switchToProfile,
    resolveDomainEntry,
  };
}
